import { useState } from "react";
import { Plus, Search, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePersons, usePersonSearch } from "@/features/pessoas/hooks/usePersons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePermissoes } from "@/contexts/PermissoesContext";
import { FORMULARIOS } from "@/domain/formularios";
import type { Cliente } from "@/features/pessoas/types";

const PAGE_SIZES = [10, 20, 50, 100] as const;

const Persons = () => {
  const navigate = useNavigate();
  const { temPermissao } = usePermissoes();
  const podeIncluir = temPermissao(FORMULARIOS.PESSOAS_EMPRESAS, 'incluir');

  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter]   = useState<string>("all");
  const [pagina, setPagina]           = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<number>(20);

  const isSearching = searchQuery.trim().length >= 2;

  // ── Paginação server-side (sem busca) ─────────────────────────────────
  const {
    persons: pagedPersons,
    total,
    isLoading: isLoadingPaged,
    isFetching,
  } = usePersons({ pagina, tamanhoPagina });

  // ── Busca server-side (com termo) ─────────────────────────────────────
  const { data: searchResults = [], isLoading: isLoadingSearch } = usePersonSearch(
    searchQuery.trim(),
    isSearching
  );

  const isLoading = isSearching ? isLoadingSearch : isLoadingPaged;

  // ── Fonte de dados e filtro por tipo ──────────────────────────────────
  const source: Cliente[] = isSearching ? searchResults : pagedPersons;

  const displayPersons = source.filter((p) => {
    if (typeFilter === "all")         return true;
    if (typeFilter === "cliente")     return p.isCliente;
    if (typeFilter === "fornecedor")  return p.isFornecedor;
    if (typeFilter === "funcionario") return p.isFuncionario;
    return true;
  });

  // ── Paginação ─────────────────────────────────────────────────────────
  const totalPages     = isSearching ? 1 : Math.max(1, Math.ceil(total / tamanhoPagina));
  const firstItem      = isSearching ? 1 : (pagina - 1) * tamanhoPagina + 1;
  const lastItem       = isSearching
    ? displayPersons.length
    : Math.min(pagina * tamanhoPagina, total);
  const totalDisplay   = isSearching ? displayPersons.length : total;

  const handlePageSizeChange = (value: string) => {
    setTamanhoPagina(Number(value));
    setPagina(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPagina(1);
  };

  // ── Tipos da pessoa ────────────────────────────────────────────────────
  const getPersonTypes = (p: Cliente) => {
    const types: string[] = [];
    if (p.isCliente)    types.push("Cliente");
    if (p.isFornecedor) types.push("Fornecedor");
    if (p.isFuncionario) types.push("Funcionário");
    return types.length > 0 ? types.join(", ") : "-";
  };

  return (
    <div className="p-6 space-y-6">
      {/* ── Cabeçalho ─────────────────────────────────────────────── */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Pessoas</h1>
          <p className="text-muted-foreground">Gerencie clientes, fornecedores e funcionários</p>
        </div>
        {podeIncluir && (
          <Button onClick={() => navigate("/pessoas/novo")}>
            <Plus className="h-4 w-4 mr-2" />
            Nova Pessoa
          </Button>
        )}
      </div>


      {/* ── Filtros ───────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, CPF/CNPJ ou email..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPagina(1); }}>
          <SelectTrigger className="w-[170px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Tipos</SelectItem>
            <SelectItem value="cliente">Clientes</SelectItem>
            <SelectItem value="fornecedor">Fornecedores</SelectItem>
            <SelectItem value="funcionario">Funcionários</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* ── Tabela ────────────────────────────────────────────────── */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>CPF/CNPJ</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Tipos</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : displayPersons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                  Nenhuma pessoa encontrada
                </TableCell>
              </TableRow>
            ) : (
              displayPersons.map((person) => (
                <TableRow
                  key={person.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/pessoas/${person.id}`)}
                >
                  <TableCell className="font-medium">{person.nomeRazao}</TableCell>
                  <TableCell>{person.cpfCnpj || "-"}</TableCell>
                  <TableCell>{person.email || "-"}</TableCell>
                  <TableCell>{person.celular || person.fone || "-"}</TableCell>
                  <TableCell>{getPersonTypes(person)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); navigate(`/pessoas/${person.id}`); }}
                    >
                      Ver Detalhes
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* ── Rodapé de paginação ───────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Contador */}
        <p className="text-sm text-muted-foreground">
          {isSearching
            ? `${displayPersons.length} resultado${displayPersons.length !== 1 ? "s" : ""} para "${searchQuery}"`
            : total === 0
              ? "Nenhum registro"
              : `Mostrando ${firstItem}–${lastItem} de ${totalDisplay} registro${totalDisplay !== 1 ? "s" : ""}`
          }
          {isFetching && !isLoading && (
            <span className="ml-2 text-xs">(atualizando...)</span>
          )}
        </p>

        {/* Controles de paginação (só quando não está buscando) */}
        {!isSearching && (
          <div className="flex items-center gap-4">
            {/* Itens por página */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
              <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[80px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((s) => (
                    <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Navegação */}
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPagina(1)}
                disabled={pagina === 1}
              >
                «
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPagina((p) => p - 1)}
                disabled={pagina === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              {/* Páginas numeradas (máx 5 botões) */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page: number;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (pagina <= 3) {
                  page = i + 1;
                } else if (pagina >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = pagina - 2 + i;
                }
                return (
                  <Button
                    key={page}
                    variant={pagina === page ? "default" : "outline"}
                    size="icon"
                    className="h-8 w-8 text-xs"
                    onClick={() => setPagina(page)}
                  >
                    {page}
                  </Button>
                );
              })}

              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPagina((p) => p + 1)}
                disabled={pagina >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPagina(totalPages)}
                disabled={pagina >= totalPages}
              >
                »
              </Button>
            </div>

            {/* Página atual / total */}
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              Página {pagina} de {totalPages}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Persons;
